import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import i18n from '../../i18n/i18n';
import {
  ExamByIdDetailsResponse,
  ExamDetailsResponse,
  ExamState,
  ExamSubjectDetailsResponse,
  PublishExamResponse,
  CompleteExamResponse,
} from '../../interface/examDetails.interface';
import { IExamReportCardResponse } from '../../interface/reportCard.interface';
import { FetchReusablePapersResponse } from '../../interface/examPaper.interface';
import { ExamPaperItemType } from '../../constant/examPaper.constant';
import {
  fetchExams,
  fetchExamById,
  createExam,
  updateExamAction,
  fetchExamSubjectById,
  publishExam,
  releaseExam,
  cancelExam,
  fetchExamAcknowledgements,
  fetchExamReportCard,
  fetchReusableExamPapers,
  fetchExamPaper,
  fetchExamPaperById,
  upsertExamPaperAction,
  cloneExamPaperAction,
  startMarkingExamSubjectAction,
  updateExamSubjectMarks,
  completeMarkingExamSubjectAction,
  fetchAttemptForMarking,
  updateExamItemMark,
  updateExamAttemptMarks,
} from '../action/exam.action';
import {
  GetAttemptForMarkingResponse,
  PatchExamItemMarkResponse,
  PatchExamAttemptMarksResponse,
  PatchExamAttemptMarkEntry,
} from '../../interface/examMarking.interface';

const initialState: ExamState = {
  exams: [],
  selectedExam: undefined,
  isLoading: false,
  isViewLoading: false,
  total: 0,
  error: undefined,
  selectedExamSubject: undefined,
  isExamSubjectLoading: false,
  acknowledgements: [],
  acknowledgementsTotal: 0,
  isLoadingAcknowledgements: false,
  acknowledgementsError: undefined,
  reportCard: null,
  reportCardLoading: false,
  reportCardError: undefined,
  reportCardErrorCode: undefined,
  reusablePapers: [],
  reusablePapersTotal: 0,
  reusablePapersSubjectAssignId: undefined,
  isLoadingReusablePapers: false,
  isLoadingPaper: false,
  isSavingPaper: false,
  isCloningPaper: false,
  isLoadingPaperById: false,
  paperError: undefined,
  markingActionLoading: false,
  marksSaveLoading: false,
  attemptMarking: null,
  isAttemptMarkingLoading: false,
  attemptMarkingError: undefined,
  attemptMarkingErrorStatus: undefined,
};

const examSlice = createSlice({
  name: 'exam',
  initialState,
  reducers: {
    resetSelectedExam(state) {
      state.selectedExam = undefined;
      state.selectedExamSubject = undefined;
    },
    // The report-card page dispatches this on mount and unmount so a previous
    // student's card can never flash on screen for the next one.
    resetExamReportCard(state) {
      state.reportCard = null;
      state.reportCardLoading = false;
      state.reportCardError = undefined;
      state.reportCardErrorCode = undefined;
    },
    resetReusableExamPapers(state) {
      state.reusablePapers = [];
      state.reusablePapersTotal = 0;
    },
    // CVA-4197 plan 0011 slice 4 — dispatched on mount/unmount of the marking
    // canvas so navigating between students (Prev/Next, slice 6) can never
    // flash the previous student's answers before the new fetch resolves.
    resetAttemptForMarking(state) {
      state.attemptMarking = null;
      state.isAttemptMarkingLoading = false;
      state.attemptMarkingError = undefined;
      state.attemptMarkingErrorStatus = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExams.pending, (state) => {
        state.isViewLoading = true;
        state.total = 0;
        state.exams = [];
        state.error = undefined;
      })
      .addCase(fetchExams.fulfilled, (state, action: PayloadAction<ExamDetailsResponse>) => {
        state.isViewLoading = false;
        state.exams = action.payload.data.exams;
        state.total = action.payload.data.total;
        state.error = undefined;
      })
      .addCase(fetchExams.rejected, (state, action) => {
        state.isViewLoading = false;
        state.exams = [];
        state.total = 0;
        state.error = action.payload as string;
      })
      .addCase(fetchExamById.pending, (state) => {
        state.isLoading = true;
        state.selectedExam = undefined;
        state.error = undefined;
      })
      .addCase(fetchExamById.fulfilled, (state, action: PayloadAction<ExamByIdDetailsResponse>) => {
        state.isLoading = false;
        state.selectedExam = action.payload.data.exam;
        state.error = undefined;
      })
      .addCase(fetchExamById.rejected, (state, action) => {
        state.isLoading = false;
        state.selectedExam = undefined;
        state.error = action.payload as string;
      })
      .addCase(createExam.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(createExam.fulfilled, (state, _action) => {
        state.isLoading = false;
        // Optionally, push the new exam to exams array or refetch list
      })
      .addCase(createExam.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_create_exam', 'Failed to create exam.');
      })
      .addCase(updateExamAction.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(updateExamAction.fulfilled, (state, _action) => {
        state.isLoading = false;
        state.error = undefined;
      })
      .addCase(updateExamAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload.message || i18n.t('toast.failed_to_update_exam', 'Failed to update exam');
      })
      .addCase(fetchExamSubjectById.pending, (state) => {
        state.isExamSubjectLoading = true;
        state.selectedExamSubject = undefined;
      })
      .addCase(
        fetchExamSubjectById.fulfilled,
        (state, action: PayloadAction<ExamSubjectDetailsResponse>) => {
          state.isExamSubjectLoading = false;
          state.selectedExamSubject = action.payload.data;
        },
      )
      .addCase(fetchExamSubjectById.rejected, (state, _action) => {
        state.isExamSubjectLoading = false;
        state.selectedExamSubject = undefined;
      })
      /* ------------------------- Marking (grading) ------------------------ *
       * Mutation responses are thin ({ updatedCount }, { examSubject: {...} }),
       * so nothing is merged into state — the page re-dispatches
       * fetchExamSubjectById on success, matching this module's existing
       * refetch-after-mutate convention.
       * -------------------------------------------------------------------- */
      .addCase(startMarkingExamSubjectAction.pending, (state) => {
        state.markingActionLoading = true;
      })
      .addCase(startMarkingExamSubjectAction.fulfilled, (state) => {
        state.markingActionLoading = false;
      })
      .addCase(startMarkingExamSubjectAction.rejected, (state) => {
        state.markingActionLoading = false;
      })
      .addCase(completeMarkingExamSubjectAction.pending, (state) => {
        state.markingActionLoading = true;
      })
      .addCase(completeMarkingExamSubjectAction.fulfilled, (state) => {
        state.markingActionLoading = false;
      })
      .addCase(completeMarkingExamSubjectAction.rejected, (state) => {
        state.markingActionLoading = false;
      })
      .addCase(updateExamSubjectMarks.pending, (state) => {
        state.marksSaveLoading = true;
      })
      .addCase(updateExamSubjectMarks.fulfilled, (state) => {
        state.marksSaveLoading = false;
      })
      .addCase(updateExamSubjectMarks.rejected, (state) => {
        state.marksSaveLoading = false;
      })
      .addCase(publishExam.pending, (state) => {
        state.isExamSubjectLoading = true;
      })
      .addCase(publishExam.fulfilled, (state, _action: PayloadAction<PublishExamResponse>) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(publishExam.rejected, (state, _action) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(releaseExam.pending, (state) => {
        state.isExamSubjectLoading = true;
      })
      .addCase(releaseExam.fulfilled, (state, _action: PayloadAction<CompleteExamResponse>) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(releaseExam.rejected, (state, _action) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(cancelExam.pending, (state) => {
        state.isExamSubjectLoading = true;
      })
      .addCase(cancelExam.fulfilled, (state) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(cancelExam.rejected, (state, _action) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(fetchExamAcknowledgements.pending, (state) => {
        state.isLoadingAcknowledgements = true;
        state.acknowledgementsError = undefined;
      })
      .addCase(fetchExamAcknowledgements.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoadingAcknowledgements = false;
        state.acknowledgements = action.payload.data.acknowledgements;
        state.acknowledgementsTotal = action.payload.data.total;
        state.acknowledgementsError = undefined;
      })
      .addCase(fetchExamAcknowledgements.rejected, (state, action) => {
        state.isLoadingAcknowledgements = false;
        state.acknowledgements = [];
        state.acknowledgementsTotal = 0;
        state.acknowledgementsError =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_acknowledgements', 'Failed to fetch acknowledgements.');
      })
      .addCase(fetchExamReportCard.pending, (state) => {
        state.reportCardLoading = true;
        state.reportCardError = undefined;
        state.reportCardErrorCode = undefined;
      })
      .addCase(
        fetchExamReportCard.fulfilled,
        (state, action: PayloadAction<IExamReportCardResponse>) => {
          state.reportCardLoading = false;
          state.reportCard = action.payload.data;
          state.reportCardError = undefined;
          state.reportCardErrorCode = undefined;
        },
      )
      .addCase(fetchExamReportCard.rejected, (state, action) => {
        state.reportCardLoading = false;
        state.reportCard = null;
        state.reportCardError =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_the_report_card',
            tr('toast.failed_to_fetch_the_report_card', 'Failed to fetch the report card.'),
          );
        state.reportCardErrorCode = action.payload?.errorCode;
      })
      .addCase(fetchReusableExamPapers.pending, (state, action) => {
        state.isLoadingReusablePapers = true;
        state.paperError = undefined;
        // Whoever dispatched MOST RECENTLY becomes the "authoritative" target —
        // an older, still-in-flight request's eventual .fulfilled/.rejected
        // will no longer match this and gets ignored below, regardless of
        // which promise actually settles first.
        state.reusablePapersSubjectAssignId = action.meta.arg.subjectAssignId;
      })
      .addCase(
        fetchReusableExamPapers.fulfilled,
        (state, action: PayloadAction<FetchReusablePapersResponse> & { meta: any }) => {
          if (action.meta.arg.subjectAssignId !== state.reusablePapersSubjectAssignId) return;
          state.isLoadingReusablePapers = false;
          state.reusablePapers = action.payload.data.papers;
          state.reusablePapersTotal = action.payload.data.total;
        },
      )
      .addCase(fetchReusableExamPapers.rejected, (state, action) => {
        if (action.meta.arg.subjectAssignId !== state.reusablePapersSubjectAssignId) return;
        state.isLoadingReusablePapers = false;
        state.reusablePapers = [];
        state.reusablePapersTotal = 0;
        state.paperError = action.payload?.message;
      })
      .addCase(fetchExamPaper.pending, (state) => {
        state.isLoadingPaper = true;
        state.paperError = undefined;
      })
      .addCase(fetchExamPaper.fulfilled, (state) => {
        state.isLoadingPaper = false;
      })
      .addCase(fetchExamPaper.rejected, (state, action) => {
        state.isLoadingPaper = false;
        state.paperError = action.payload?.message;
      })
      .addCase(upsertExamPaperAction.pending, (state) => {
        state.isSavingPaper = true;
        state.paperError = undefined;
      })
      .addCase(upsertExamPaperAction.fulfilled, (state) => {
        state.isSavingPaper = false;
      })
      .addCase(upsertExamPaperAction.rejected, (state, action) => {
        state.isSavingPaper = false;
        state.paperError =
          action.payload?.message ||
          tr('exam_paper.error_save_paper', 'Failed to save exam paper.');
      })
      .addCase(cloneExamPaperAction.pending, (state) => {
        state.isCloningPaper = true;
        state.paperError = undefined;
      })
      .addCase(cloneExamPaperAction.fulfilled, (state) => {
        state.isCloningPaper = false;
      })
      .addCase(cloneExamPaperAction.rejected, (state, action) => {
        state.isCloningPaper = false;
        state.paperError =
          action.payload?.message ||
          tr('exam_paper.error_clone_paper', 'Failed to clone exam paper.');
      })
      .addCase(fetchExamPaperById.pending, (state) => {
        state.isLoadingPaperById = true;
        state.paperError = undefined;
      })
      .addCase(fetchExamPaperById.fulfilled, (state) => {
        state.isLoadingPaperById = false;
      })
      .addCase(fetchExamPaperById.rejected, (state, action) => {
        state.isLoadingPaperById = false;
        state.paperError =
          action.payload?.message ||
          tr('exam_paper.error_fetch_selected_paper', 'Failed to fetch the selected paper.');
      })
      .addCase(fetchAttemptForMarking.pending, (state) => {
        state.isAttemptMarkingLoading = true;
        state.attemptMarking = null;
        state.attemptMarkingError = undefined;
        state.attemptMarkingErrorStatus = undefined;
      })
      .addCase(
        fetchAttemptForMarking.fulfilled,
        (state, action: PayloadAction<GetAttemptForMarkingResponse>) => {
          state.isAttemptMarkingLoading = false;
          state.attemptMarking = action.payload.data;
        },
      )
      .addCase(fetchAttemptForMarking.rejected, (state, action) => {
        state.isAttemptMarkingLoading = false;
        state.attemptMarking = null;
        state.attemptMarkingError =
          action.payload?.message ||
          i18n.t('exam_marking.err_load_attempt', "Failed to load this student's attempt.");
        state.attemptMarkingErrorStatus = action.payload?.status;
      })
      /*
       * CVA-4197 plan 0011 slice 5 — write endpoints. Loading/error is the
       * page's own local UI state (per-card vs. rail loading spinners), not
       * modeled here; only the SUCCESS path matters to global state, and only
       * to patch `attemptMarking` in place rather than refetching the whole
       * payload.
       */
      .addCase(
        updateExamItemMark.fulfilled,
        (
          state,
          action: PayloadAction<PatchExamItemMarkResponse> & { meta: { arg: { itemId: string } } },
        ) => {
          const attempt = state.attemptMarking;
          if (!attempt) return;
          const item = attempt.items.find((i) => i.id === action.meta.arg.itemId);
          if (!item) return;
          // Bug CVA-4763 — this endpoint never actually changes an MCQ
          // item's score/isAutoScored (the backend always derives them
          // live and never persists an override for MCQ), but its raw
          // response still carries whatever it wrote to the DB row
          // (`score: null` — the only shape a save against MCQ can take,
          // since an explicit score is rejected). Trusting that response
          // wholesale here is what made a correct MCQ auto-score flip to
          // "still needs a score" the instant ANY save touched it, no
          // reload needed. Only `feedback` is a genuine change for MCQ.
          if (item.itemType === ExamPaperItemType.MCQ) {
            if (item.mark) item.mark.feedback = action.payload.data.feedback;
            return;
          }
          item.mark = action.payload.data;
        },
      )
      /*
       * The bulk endpoint only returns `{ updatedCount }` — no per-item echo.
       * Since it validates every item before writing any (all-or-nothing), a
       * 200 here means every item in the sent request is now persisted
       * exactly as sent, so it is safe to patch local state from the request
       * body itself rather than refetching.
       */
      .addCase(
        updateExamAttemptMarks.fulfilled,
        (
          state,
          action: PayloadAction<PatchExamAttemptMarksResponse> & {
            meta: { arg: { body: { items: PatchExamAttemptMarkEntry[] } } };
          },
        ) => {
          const attempt = state.attemptMarking;
          if (!attempt) return;
          const now = new Date().toISOString();
          action.meta.arg.body.items.forEach((sent) => {
            const item = attempt.items.find((i) => i.id === sent.itemId);
            if (!item) return;
            // Bug CVA-4763 — the bulk rail always includes EVERY scorable
            // item, MCQ included, even when only one card was actually
            // edited (`sent.score` is always `undefined` for MCQ, per
            // CVA-4742). Patching `item.mark` from the sent payload here,
            // same as the per-item reducer above, turned that into an
            // immediate (no reload needed) score: null for the MCQ item on
            // every bulk save. Preserve its existing score/isAutoScored —
            // only feedback can legitimately change for MCQ.
            if (item.itemType === ExamPaperItemType.MCQ) {
              if (item.mark) item.mark.feedback = sent.feedback ?? null;
              return;
            }
            const score = sent.score ?? null;
            item.mark = {
              score,
              feedback: sent.feedback ?? null,
              isAutoScored: false,
              // Mirrors the backend's own rule (bulkUpsertExamItemMarks):
              // an explicitly-cleared item (score: null) is not "marked".
              markedAt: score != null ? now : null,
            };
          });
        },
      );
  },
});

export const {
  resetSelectedExam,
  resetExamReportCard,
  resetReusableExamPapers,
  resetAttemptForMarking,
} = examSlice.actions;
export default examSlice.reducer;
